import { zodResolver } from '@hookform/resolvers/zod';
import {
  PORTAL_ACCESS_MODES,
  PortalAccessModeSchema,
  PortalCategorySchema,
  type Project,
  TERMINOLOGIES,
  TerminologySchema,
} from '@servicetim/shared';
import { useQueryKeys, useUpdateProject } from '@servicetim/ui/api';
import {
  AsyncPicker,
  Button,
  ConflictDialog,
  Input,
  ProblemBanner,
  toast,
  useConflictHandler,
  useUserOptionLoader,
} from '@servicetim/ui/components';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { applyProblemErrors } from '../lib/rhf-errors';
import { FormRow } from '../ui/form-row';
import { Section } from '../ui/section';
import { SimpleSelect } from '../ui/simple-select';
import { PortalCategoriesEditor } from './portal-categories-editor';

const SettingsFormSchema = z.object({
  name: z.string().trim().min(1, 'A name is required').max(120),
  category: z.string().trim().max(80),
  leadId: z.string().nullable(),
  terminology: TerminologySchema,
  portalAccessMode: PortalAccessModeSchema,
  portalCategories: z.array(PortalCategorySchema).max(50),
});
export type SettingsFormValues = z.infer<typeof SettingsFormSchema>;

const TERMINOLOGY_LABELS: Record<(typeof TERMINOLOGIES)[number], string> = {
  issue: 'Issue',
  request: 'Request',
  task: 'Task',
};
const ACCESS_MODE_LABELS: Record<(typeof PORTAL_ACCESS_MODES)[number], string> = {
  invite_only: 'Invite only',
  tenant: 'Everyone in the tenant',
  public: 'Public (anyone with the link)',
};

const toValues = (project: Project): SettingsFormValues => ({
  name: project.name,
  category: project.category ?? '',
  leadId: project.leadId,
  terminology: project.settings.terminology,
  portalAccessMode: project.settings.portalAccessMode,
  portalCategories: project.settings.portalCategories,
});

/**
 * Name, category, lead, terminology, portal access and portal categories. Saves with
 * `If-Match: configVersion`; a 409 opens the reload-and-retry dialog and the retry re-reads the
 * latest version from the query cache.
 */
export function ProjectSettingsForm({
  project,
  refetch,
}: {
  project: Project;
  refetch: () => Promise<unknown>;
}) {
  const id = useId();
  const keys = useQueryKeys();
  const queryClient = useQueryClient();
  const update = useUpdateProject();
  const conflict = useConflictHandler(refetch);
  const loadUsers = useUserOptionLoader();
  const [omitPaths, setOmitPaths] = useState<Set<string>>(new Set());

  const form = useForm<SettingsFormValues>({
    resolver: zodResolver(SettingsFormSchema),
    defaultValues: toValues(project),
  });
  // Re-sync when the project changes underneath (reload after conflict, other tab saved).
  useEffect(() => {
    if (!form.formState.isDirty) form.reset(toValues(project));
  }, [project, form]);

  const errors = form.formState.errors;
  const terminologyOptions = useMemo(
    () => TERMINOLOGIES.map((t) => ({ value: t, label: TERMINOLOGY_LABELS[t] })),
    [],
  );
  const accessOptions = useMemo(
    () => PORTAL_ACCESS_MODES.map((m) => ({ value: m, label: ACCESS_MODE_LABELS[m] })),
    [],
  );

  const submit = form.handleSubmit(async (values) => {
    setOmitPaths(new Set());
    try {
      const saved = await conflict.guard(() => {
        const latest = queryClient.getQueryData<Project>(keys.projects.detail(project.id));
        return update.mutateAsync({
          id: project.id,
          ifMatch: latest?.configVersion ?? project.configVersion,
          patch: {
            name: values.name,
            category: values.category === '' ? null : values.category,
            leadId: values.leadId,
            settings: {
              terminology: values.terminology,
              portalAccessMode: values.portalAccessMode,
              portalCategories: values.portalCategories,
            },
          },
        });
      });
      if (saved) {
        form.reset(toValues(saved));
        toast.success('Project settings saved');
      }
    } catch (caught) {
      setOmitPaths(
        applyProblemErrors(caught, form.setError, {
          rename: { 'settings.': '' },
          known: [
            'name',
            'category',
            'leadId',
            'terminology',
            'portalAccessMode',
            'portalCategories',
          ],
        }),
      );
    }
  });

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void submit(event);
      }}
      className="space-y-6"
    >
      {update.error && !conflict.conflict ? (
        <ProblemBanner error={update.error} omitPaths={omitPaths} size="sm" />
      ) : null}
      <Section title="General" description="How the project appears to agents and requesters.">
        <div className="grid gap-4 sm:grid-cols-2">
          <FormRow label="Name" htmlFor={`${id}-name`} required error={errors.name?.message}>
            <Input id={`${id}-name`} {...form.register('name')} maxLength={120} />
          </FormRow>
          <FormRow label="Category" htmlFor={`${id}-category`} error={errors.category?.message}>
            <Input id={`${id}-category`} {...form.register('category')} maxLength={80} />
          </FormRow>
          <Controller
            control={form.control}
            name="leadId"
            render={({ field, fieldState }) => (
              <FormRow
                label="Project lead"
                htmlFor={`${id}-lead`}
                error={fieldState.error?.message}
              >
                <AsyncPicker
                  id={`${id}-lead`}
                  value={field.value}
                  onChange={(next) => field.onChange(next ?? null)}
                  load={loadUsers}
                  placeholder="No lead"
                />
              </FormRow>
            )}
          />
          <Controller
            control={form.control}
            name="terminology"
            render={({ field }) => (
              <FormRow
                label="Terminology"
                htmlFor={`${id}-terminology`}
                hint="The word used for work items in this project."
              >
                <SimpleSelect
                  id={`${id}-terminology`}
                  value={field.value}
                  onChange={(next) => next && field.onChange(next)}
                  options={terminologyOptions}
                />
              </FormRow>
            )}
          />
        </div>
      </Section>
      <Section title="Portal" description="Who can raise requests and how they are grouped.">
        <Controller
          control={form.control}
          name="portalAccessMode"
          render={({ field }) => (
            <FormRow label="Access mode" htmlFor={`${id}-access`} className="max-w-sm">
              <SimpleSelect
                id={`${id}-access`}
                value={field.value}
                onChange={(next) => next && field.onChange(next)}
                options={accessOptions}
              />
            </FormRow>
          )}
        />
        <PortalCategoriesEditor form={form} />
      </Section>
      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          disabled={!form.formState.isDirty || update.isPending}
          onClick={() => form.reset(toValues(project))}
        >
          Discard changes
        </Button>
        <Button type="submit" disabled={update.isPending || !form.formState.isDirty}>
          Save settings
        </Button>
      </div>
      <ConflictDialog {...conflict.dialogProps} subject="this project" />
    </form>
  );
}
